import React, { useEffect, useState } from "react";
import { Plus, Download, Upload, Trash2, CreditCard, Camera, Activity } from "lucide-react";
import { toast } from "sonner";
import { API, fmtErr, money, fmtDate } from "../../lib/api";
import { ContactDrop } from "../../components/ContactDrop";
import { ContractTemplatesCard } from "../../components/ContractTemplatesCard";
import { WhatsAppSenderCard } from "../../components/WhatsAppSenderCard";
import { useLang } from "../../lib/i18n";
import { useAuth, isManagerUp } from "../../lib/auth";

const INTEREST_CATS = ["Jewelry", "Watches", "Electronics", "Car", "Motorcycle", "Heavy Equipment", "Tools", "Other"];
import { shopLogoUrl } from "../../lib/branding";
import { StatusBadge } from "../../components/StatusBadge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

export default function Settings() {
  const { t } = useLang();
  const { user, setUser } = useAuth();
  const isOwner = user?.role === "shop_owner";
  const managerUp = isManagerUp(user);
  const [billing, setBilling] = useState(null);
  const [users, setUsers] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [staffOpen, setStaffOpen] = useState(false);
  const [planId, setPlanId] = useState("");
  const [cycle, setCycle] = useState("monthly");
  const [newPayment, setNewPayment] = useState(null);
  const [staff, setStaff] = useState({ name: "", email: "", password: "", role: "cashier" });
  const [profile, setProfile] = useState({ company_address: "", company_phone: "", license_no: "", contract_terms: "", header_text: "", footer_text: "", brand_color: "#1E3A5F", interest_default_rate: "", interest_min_rate: "", interest_max_rate: "", interest_category_rates: {} });
  const [logoPath, setLogoPath] = useState("");
  const [activity, setActivity] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadBilling = () => API.get("/shop/billing").then((r) => {
    setBilling(r.data);
    setPlanId(r.data.plan?.id || "");
    setCycle(r.data.subscription?.billing_cycle || "monthly");
    const s = r.data.shop || {};
    setProfile({
      company_address: s.company_address || "", company_phone: s.company_phone || "",
      interest_default_rate: s.interest_default_rate ?? "", interest_category_rates: s.interest_category_rates || {},
      interest_min_rate: s.interest_min_rate ?? "", interest_max_rate: s.interest_max_rate ?? "",
      license_no: s.license_no || "", contract_terms: s.contract_terms || "",
      header_text: s.header_text || "", footer_text: s.footer_text || "",
      brand_color: s.brand_color || "#1E3A5F",
    });
    setLogoPath(s.logo_path || "");
  }).catch(() => {});
  const loadUsers = () => API.get("/shop/users").then((r) => setUsers(r.data)).catch(() => {});

  useEffect(() => {
    loadBilling();
    if (billing?.shop?.status === "active") {
      loadUsers();
      API.get("/shop/activity").then((r) => setActivity(r.data)).catch(() => {});
    }
    // eslint-disable-next-line
  }, [billing?.shop?.status]);

  if (!billing) return <p className="text-slate-500">{t("common.loading")}</p>;

  const { shop, subscription, plan, payments, bank, bank_accounts, plans } = billing;
  const pending = payments.find((p) => p.status === "pending");

  const submitPayment = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await API.post("/shop/billing/payments", { plan_id: planId, billing_cycle: cycle });
      setNewPayment(data);
      loadBilling();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const addStaff = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await API.post("/shop/users", staff);
      toast.success(t("msg.created"));
      setStaffOpen(false);
      setStaff({ name: "", email: "", password: "", role: "cashier" });
      loadUsers();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...profile,
        interest_default_rate: profile.interest_default_rate === "" ? null : profile.interest_default_rate,
        interest_min_rate: profile.interest_min_rate === "" ? null : profile.interest_min_rate,
        interest_max_rate: profile.interest_max_rate === "" ? null : profile.interest_max_rate };
      await API.patch("/shop/profile", payload);
      toast.success(t("msg.saved"));
      loadBilling();
      API.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const removeStaff = async (u) => {
    if (!window.confirm(`${t("common.delete")} ${u.name}?`)) return;
    try {
      await API.delete(`/shop/users/${u.id}`);
      toast.success(t("msg.deleted"));
      loadUsers();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const data = new FormData();
    data.append("file", file);
    setBusy(true);
    try {
      const r = await API.post("/shop/logo", data);
      setLogoPath(r.data.logo_path);
      toast.success(t("msg.saved"));
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const downloadBackup = async () => {
    try {
      const r = await API.get("/shop/backup", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      const cd = r.headers["content-disposition"] || "";
      a.href = url;
      a.download = (cd.match(/filename="?([^";]+)/) || [])[1] || "pawno-backup.json";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("msg.backup_downloaded"));
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const restoreBackup = async (file) => {
    if (!file) return;
    if (!window.confirm(t("settings.restore_warn"))) return;
    const data = new FormData();
    data.append("file", file);
    setBusy(true);
    try {
      await API.post("/shop/restore", data);
      toast.success(t("msg.restored"));
      loadBilling();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const BankPanel = ({ bank: b, accounts, reference, amount }) => {
    const list = accounts?.length ? accounts : (b ? [b] : []);
    return (
      <div className="space-y-3" data-testid="settings-bank-panel">
        {list.length > 1 && <p className="text-xs text-slate-500">{t("bank.multi_note")}</p>}
        {list.map((acc, i) => (
          <div key={acc?.id || i} className="rounded-lg border border-slate-200 divide-y text-sm" data-testid={`settings-bank-account-${i}`}>
            <div className="flex justify-between p-3"><span className="text-slate-500">{t("bank.name")}</span><span className="font-medium">{acc?.bank_name}</span></div>
            <div className="flex justify-between p-3"><span className="text-slate-500">{t("bank.acc_name")}</span><span className="font-medium">{acc?.account_name}</span></div>
            <div className="flex justify-between p-3"><span className="text-slate-500">{t("bank.acc_no")}</span><span className="font-mono font-medium">{acc?.account_number}</span></div>
            {acc?.iban && <div className="flex justify-between p-3"><span className="text-slate-500">{t("bank.iban")}</span><span className="font-mono font-medium">{acc.iban}</span></div>}
            {acc?.swift && <div className="flex justify-between p-3"><span className="text-slate-500">{t("bank.swift")}</span><span className="font-mono font-medium">{acc.swift}</span></div>}
          </div>
        ))}
        {(reference || amount != null) && (
          <div className="rounded-lg border border-amber-200 divide-y text-sm">
            {reference && <div className="flex justify-between p-3 bg-amber-50"><span className="text-amber-800">{t("landing.reference")}</span><span className="font-mono font-bold text-amber-900">{reference}</span></div>}
            {amount != null && <div className="flex justify-between p-3 bg-amber-50"><span className="text-amber-800">{t("landing.amount_due")}</span><span className="font-mono font-bold text-amber-900">{money(amount)}</span></div>}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900">{t("settings.title")}</h1>

      {shop.status !== "active" && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-6" data-testid="pending-banner">
          <h3 className="font-heading text-lg font-semibold text-amber-900">{t("settings.pending_banner")}</h3>
          <p className="text-sm text-amber-800 mt-1 mb-4">{t("settings.pending_text")}</p>
          <BankPanel bank={bank} accounts={bank_accounts} reference={pending?.reference} amount={pending?.amount} />

        <div
          data-testid="pending-receipt-instructions"
          className="mt-4 rounded-lg border border-amber-200 bg-white p-3 text-sm space-y-2"
        >
          <p className="font-semibold text-slate-900">
            Bank Transfer Receipt Required
          </p>
          <p className="text-slate-700">
            After completing your bank transfer, email the receipt to
            <strong> service@kinos.tl</strong> and include your payment
            reference. Your subscription will be activated only after
            Super Admin verification.
          </p>
          <a
            data-testid="pending-email-receipt"
            href={`mailto:service@kinos.tl?subject=${encodeURIComponent(
              `SAS Penhores Subscription Payment - ${pending?.reference}`
            )}&body=${encodeURIComponent(
              `Payment reference: ${pending?.reference}\nAmount: USD ${pending?.amount}\n\nPlease find my bank-transfer receipt attached.`
            )}`}
            className="inline-block rounded-lg bg-slate-900 px-4 py-2 text-white hover:bg-slate-800"
          >
            Send Receipt by Email
          </a>
          <p className="text-xs text-slate-500">
            Attach your bank-transfer receipt before sending the email.
          </p>
        </div>

        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-medium text-slate-900">{t("settings.subscription")}</h3>
            {isOwner && (
              <Button data-testid="new-payment-button" size="sm" onClick={() => { setNewPayment(null); setPayOpen(true); }} disabled={!!pending}
                className="bg-emerald-600 hover:bg-emerald-700 text-white">
                {t("settings.renew")}
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <span className="text-slate-500">{t("settings.current_plan")}</span>
            <span className="font-semibold" data-testid="current-plan-label">{plan?.name} <StatusBadge status={shop.status} /></span>
            <span className="text-slate-500">{t("settings.period_end")}</span>
            <span className="font-mono" data-testid="period-end-label">{subscription?.current_period_end ? fmtDate(subscription.current_period_end) : "—"}</span>
          </div>
          {pending && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm flex justify-between items-center">
              <span className="text-amber-800">{t("landing.reference")}: <span className="font-mono font-bold">{pending.reference}</span></span>
              <StatusBadge status="pending" />
            </div>
          )}
        </div>

        {shop.status === "active" && (
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-medium text-slate-900">{t("settings.staff")}</h3>
              {isOwner && (
                <Button data-testid="add-staff-button" size="sm" variant="outline" onClick={() => setStaffOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" />{t("settings.add_staff")}
                </Button>
              )}
            </div>
            <div className="space-y-2">
              {users.map((u) => (
                <div key={u.id} className="flex justify-between items-center text-sm border border-slate-200 rounded-lg px-3 py-2" data-testid={`staff-row-${u.id.slice(0, 8)}`}>
                  <span className="font-medium">{u.name}</span>
                  <span className="text-slate-500 flex items-center gap-2">
                    {u.email} · {t(`role.${u.role}`)}
                    {isOwner && u.role !== "shop_owner" && (
                      <button data-testid={`remove-staff-${u.id.slice(0, 8)}`} onClick={() => removeStaff(u)} className="p-1 hover:bg-red-50 rounded">
                        <Trash2 className="h-3.5 w-3.5 text-red-600" />
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {isOwner && shop.status === "active" && (
        <form onSubmit={saveProfile} className="bg-white rounded-xl border border-slate-200 p-6" data-testid="company-profile-form">
          <h3 className="font-heading text-lg font-medium text-slate-900 mb-4">{t("settings.company")}</h3>
          <div className="grid lg:grid-cols-3 gap-3">
            <div>
              <Label>{t("settings.company_address")}</Label>
              <Input data-testid="company-address-input" value={profile.company_address}
                onChange={(e) => setProfile({ ...profile, company_address: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>{t("settings.company_phone")}</Label>
              <Input data-testid="company-phone-input" value={profile.company_phone}
                onChange={(e) => setProfile({ ...profile, company_phone: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>{t("settings.license")}</Label>
              <Input data-testid="company-license-input" value={profile.license_no}
                onChange={(e) => setProfile({ ...profile, license_no: e.target.value })} className="mt-1" />
            </div>
          </div>
          <div className="mt-4 border-t border-slate-100 pt-4">
            <div className="text-sm font-medium text-slate-800 mb-2">{t("settings.interest")}</div>
            <div className="grid lg:grid-cols-3 gap-3 max-w-2xl">
              <div>
                <Label>{t("settings.default_rate")}</Label>
                <Input data-testid="default-rate-input" type="number" min="0" max="100" step="0.1"
                  value={profile.interest_default_rate}
                  onChange={(e) => setProfile({ ...profile, interest_default_rate: e.target.value === "" ? "" : Number(e.target.value) })}
                  className="mt-1" />
              </div>
              <div>
                <Label>{t("settings.rate_min")}</Label>
                <Input data-testid="min-rate-input" type="number" min="0" max="100" step="0.1"
                  value={profile.interest_min_rate}
                  onChange={(e) => setProfile({ ...profile, interest_min_rate: e.target.value === "" ? "" : Number(e.target.value) })}
                  className="mt-1" />
              </div>
              <div>
                <Label>{t("settings.rate_max")}</Label>
                <Input data-testid="max-rate-input" type="number" min="0" max="100" step="0.1"
                  value={profile.interest_max_rate}
                  onChange={(e) => setProfile({ ...profile, interest_max_rate: e.target.value === "" ? "" : Number(e.target.value) })}
                  className="mt-1" />
              </div>
            </div>
            <Label className="mt-3 block">{t("settings.category_rates")}</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
              {INTEREST_CATS.map((cat) => (
                <div key={cat} className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-24 truncate">{cat}</span>
                  <Input data-testid={`cat-rate-${cat.replace(/\s+/g, "-").toLowerCase()}`} type="number" min="0" max="100" step="0.1"
                    value={profile.interest_category_rates[cat] ?? ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      const next = { ...profile.interest_category_rates };
                      if (v === "") delete next[cat]; else next[cat] = Number(v);
                      setProfile({ ...profile, interest_category_rates: next });
                    }} className="h-8 text-xs" />
                  </div>
              ))}
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-3 mt-3">
            <div>
              <Label>{t("settings.logo")}</Label>
              <div className="mt-1 flex items-center gap-3">
                {logoPath && (
                  <img src={shopLogoUrl({ logo_path: logoPath })} alt="logo" data-testid="company-logo-preview"
                    className="h-12 w-12 rounded-lg object-cover border border-slate-200" />
                )}
                <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm hover:bg-slate-50">
                  <Camera className="h-4 w-4" />{t("settings.upload_logo")}
                  <input type="file" accept="image/*" className="hidden" data-testid="company-logo-input"
                    onChange={(e) => uploadLogo(e.target.files[0])} />
                </label>
              </div>
            </div>
            <div>
              <Label>{t("settings.brand_color")}</Label>
              <div className="mt-1 flex items-center gap-2">
                <input type="color" data-testid="brand-color-picker" value={profile.brand_color}
                  onChange={(e) => setProfile({ ...profile, brand_color: e.target.value })}
                  className="h-9 w-12 rounded border border-slate-300 cursor-pointer bg-white" />
                <Input data-testid="brand-color-input" value={profile.brand_color}
                  onChange={(e) => setProfile({ ...profile, brand_color: e.target.value })} className="font-mono" />
              </div>
              <Label className="mt-2 block">{t("settings.header_text")}</Label>
              <Input data-testid="header-text-input" value={profile.header_text}
                onChange={(e) => setProfile({ ...profile, header_text: e.target.value })} className="mt-1" />
              <Label className="mt-2 block">{t("settings.footer_text")}</Label>
              <Input data-testid="footer-text-input" value={profile.footer_text}
                onChange={(e) => setProfile({ ...profile, footer_text: e.target.value })} className="mt-1" />
            </div>
          </div>
          <div className="mt-3">
            <Label>{t("settings.terms")}</Label>
            <textarea data-testid="company-terms-input" rows={4} value={profile.contract_terms}
              onChange={(e) => setProfile({ ...profile, contract_terms: e.target.value })}
              className="mt-1 w-full rounded-md border border-input bg-white px-3 py-2 text-sm" />
          </div>
          <Button data-testid="company-save-button" type="submit" disabled={busy} className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white">
            {t("common.save")}
          </Button>
        </form>
      )}

      {isOwner && shop.status === "active" && <ContractTemplatesCard />}
      {managerUp && shop.status === "active" && <WhatsAppSenderCard isOwner={isOwner} />}

      {managerUp && shop.status === "active" && activity && (
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="activity-card">
          <h3 className="font-heading text-lg font-medium text-slate-900 mb-4 flex items-center gap-2">
            <Activity className="h-5 w-5 text-emerald-600" />{t("settings.activity")}
          </h3>
          <div className="grid lg:grid-cols-2 gap-6">
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">{t("settings.recent_logins")}</div>
              <div className="space-y-1.5">
                {activity.logins.length === 0 && <p className="text-sm text-slate-400">{t("common.none")}</p>}
                {activity.logins.map((l) => (
                  <div key={l.id} className="flex justify-between text-sm border border-slate-100 rounded-lg px-3 py-2" data-testid={`login-event-${l.id.slice(0, 8)}`}>
                    <span className="font-medium truncate">{l.email}</span>
                    <span className="text-slate-500 font-mono text-xs shrink-0 ml-2">
                      {l.ip || "—"} · {new Date(l.created_at).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">{t("settings.audit_highlights")}</div>
              <div className="space-y-1.5">
                {activity.audit.length === 0 && <p className="text-sm text-slate-400">{t("common.none")}</p>}
                {activity.audit.map((a) => (
                  <div key={a.id} className="flex justify-between text-sm border border-slate-100 rounded-lg px-3 py-2" data-testid={`audit-hl-${a.id.slice(0, 8)}`}>
                    <span className="truncate">{a.user_name || a.user_email} · {a.action} {a.entity}</span>
                    <span className="text-slate-500 text-xs shrink-0 ml-2">{fmtDate(a.created_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {isOwner && shop.status === "active" && (
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="backup-card">
          <h3 className="font-heading text-lg font-medium text-slate-900 mb-2">{t("settings.backup")}</h3>
          <p className="text-sm text-slate-500 mb-4">{t("settings.backup_desc")}</p>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" data-testid="backup-download-button" onClick={downloadBackup}>
              <Download className="h-4 w-4 mr-1.5" />{t("settings.download_backup")}
            </Button>
            <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-md border border-slate-300 text-sm hover:bg-slate-50" data-testid="backup-restore-label">
              <Upload className="h-4 w-4" />{t("settings.restore")}
              <input type="file" accept="application/json" className="hidden" data-testid="backup-restore-input"
                onChange={(e) => { restoreBackup(e.target.files[0]); e.target.value = ""; }} />
            </label>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-heading text-lg font-medium text-slate-900 mb-4">{t("settings.history")}</h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("common.date")}</TableHead>
              <TableHead>{t("landing.plan")}</TableHead>
              <TableHead>{t("common.amount")}</TableHead>
              <TableHead>{t("common.reference")}</TableHead>
              <TableHead>{t("common.status")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.map((p) => (
              <TableRow key={p.id} data-testid={`billing-payment-${p.reference}`}>
                <TableCell>{fmtDate(p.created_at)}</TableCell>
                <TableCell>{p.plan_name} · {p.billing_cycle}</TableCell>
                <TableCell className="font-mono">{money(p.amount)}</TableCell>
                <TableCell className="font-mono">{p.reference}</TableCell>
                <TableCell><StatusBadge status={p.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="bg-white" data-testid="new-payment-dialog">
          <DialogHeader><DialogTitle className="font-heading">{t("settings.new_payment")}</DialogTitle></DialogHeader>
          {!newPayment ? (
            <form onSubmit={submitPayment} className="space-y-4">
              <div>
                <Label>{t("landing.plan")}</Label>
                <Select value={planId} onValueChange={setPlanId}>
                  <SelectTrigger data-testid="billing-plan-select" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("landing.billing")}</Label>
                <Select value={cycle} onValueChange={setCycle}>
                  <SelectTrigger data-testid="billing-cycle-select" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="monthly">{t("landing.monthly")}</SelectItem>
                    <SelectItem value="annual">{t("landing.annual")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p data-testid="local-pay-note" className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
                {t("settings.local_pay_note")}
              </p>
              <ContactDrop />
              <Button data-testid="submit-payment-button" type="submit" disabled={busy || !planId}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">{t("common.save")}</Button>
              
            </form>
          ) : (
            <div className="space-y-4">
              <BankPanel bank={newPayment.bank} accounts={newPayment.bank_accounts} reference={newPayment.payment.reference} amount={newPayment.payment.amount} />
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm space-y-2">
                  <p className="font-semibold text-amber-900">
                    Bank Transfer Receipt Required
                  </p>
                  <p className="text-slate-700">
                    After completing your bank transfer, email your payment receipt to
                    <strong> service@kinos.tl</strong> and include your payment
                    reference. Your subscription will be activated only after
                    administrator verification.
                  </p>
                  <a
                    data-testid="new-payment-email-receipt"
                    href={`mailto:service@kinos.tl?subject=${encodeURIComponent(
                      `SAS Penhores Subscription Payment - ${newPayment.payment.reference}`
                    )}&body=${encodeURIComponent(
                      `Payment reference: ${newPayment.payment.reference}\nAmount: USD ${newPayment.payment.amount}\n\nPlease find my bank-transfer receipt attached.`
                    )}`}
                    className="inline-block rounded-lg bg-slate-900 px-4 py-2 text-white hover:bg-slate-800"
                  >
                    Send Receipt by Email
                  </a>
                  <p className="text-xs text-slate-500">
                    Please attach your transfer receipt before sending the email.
                  </p>
                </div>

              <Button data-testid="close-payment-dialog" onClick={() => setPayOpen(false)} className="w-full bg-slate-900 text-white">OK</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={staffOpen} onOpenChange={setStaffOpen}>
        <DialogContent className="bg-white" data-testid="staff-dialog">
          <DialogHeader><DialogTitle className="font-heading">{t("settings.add_staff")}</DialogTitle></DialogHeader>
          <form onSubmit={addStaff} className="space-y-4">
            <div>
              <Label>{t("common.name")}</Label>
              <Input data-testid="staff-name-input" required value={staff.name} onChange={(e) => setStaff({ ...staff, name: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>{t("landing.email")}</Label>
              <Input data-testid="staff-email-input" type="email" required value={staff.email} onChange={(e) => setStaff({ ...staff, email: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>{t("landing.password")}</Label>
              <Input data-testid="staff-password-input" type="password" required minLength={6} value={staff.password} onChange={(e) => setStaff({ ...staff, password: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>{t("settings.role")}</Label>
              <Select value={staff.role} onValueChange={(v) => setStaff({ ...staff, role: v })}>
                <SelectTrigger data-testid="staff-role-select" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="cashier" data-testid="staff-role-cashier">{t("role.shop_cashier")}</SelectItem>
                  <SelectItem value="manager" data-testid="staff-role-manager">{t("role.shop_manager")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="staff-save-button" type="submit" disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">{t("common.create")}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
